import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './LoginForm.css';
import API_CONFIG from '../utils/apiConfig';

const LoginForm = ({ onLoginSuccess }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite_token');
  
  const formLoadTimeRef = useRef(Date.now());
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    website: '' // honeypot
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resendVerificationLoading, setResendVerificationLoading] = useState(false);
  const [resendVerificationMessage, setResendVerificationMessage] = useState('');
  const [inviteInfo, setInviteInfo] = useState(null);
  const [showGdprConsentModal, setShowGdprConsentModal] = useState(false);
  const [gdprConsentGiven, setGdprConsentGiven] = useState(false);

  /** Organisation / trading name from invite (not the employer user's personal name). */
  const inviteEmployerOrgLabel = inviteInfo
    ? (inviteInfo.employer_company_name || inviteInfo.employer_name || '')
    : '';
  
  const validateInvite = useCallback(async (token) => {
    try {
      const t = token || '';
      const response = await fetch(API_CONFIG.getApiUrl(`${API_CONFIG.endpoints.validateInviteToken}/${t}`));
      if (response.ok) {
        const data = await response.json();
        setInviteInfo(data);
        setError('');
        if (data.email) {
          setFormData(prev => ({ ...prev, email: data.email }));
        }
      } else {
        setInviteInfo(null);
        const errorData = await response.json().catch(() => ({}));
        setError(
          errorData.detail ||
            'This invitation is not valid. It may have expired or been withdrawn by the employer.'
        );
      }
    } catch (err) {
      setInviteInfo(null);
      setError('Failed to validate invitation. Please try again.');
    }
  }, []);

  // Validate invite token on mount if present
  useEffect(() => {
    if (inviteToken) {
      validateInvite(inviteToken);
    }
  }, [inviteToken, validateInvite]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error message when user starts typing
    if (error) {
      setError('');
      setShowResendVerification(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (formData.website) {
      setForgotPasswordMessage('Invalid submission. Please try again.');
      return;
    }
    const elapsedSec = (Date.now() - formLoadTimeRef.current) / 1000;
    if (elapsedSec < 3) {
      setForgotPasswordMessage('Please wait a moment before requesting a reset.');
      return;
    }
    setForgotPasswordLoading(true);
    setForgotPasswordMessage('');

    try {
      const response = await fetch(API_CONFIG.getApiUrl('/auth/forgot-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: forgotPasswordEmail,
          form_load_time: formLoadTimeRef.current
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setForgotPasswordMessage(data.message || 'Password reset instructions have been sent to your email.');
        setForgotPasswordEmail('');
        setTimeout(() => {
          setShowForgotPassword(false);
          setForgotPasswordMessage('');
        }, 3000);
      } else {
        const errorData = await response.json();
        // Handle different error formats
        let errorMessage = 'Failed to send reset email. Please try again.';
        
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (Array.isArray(errorData.detail)) {
            // Handle validation errors array
            errorMessage = errorData.detail.map(err => err.msg || err).join(', ');
          }
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
        
        setForgotPasswordMessage(errorMessage);
      }
    } catch (err) {
      setForgotPasswordMessage('Network error. Please try again.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendVerificationLoading(true);
    setResendVerificationMessage('');

    try {
      const response = await fetch(API_CONFIG.getApiUrl(API_CONFIG.endpoints.resendVerification), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: formData.email }),
      });

      if (response.ok) {
        const data = await response.json();
        const remainingResends = data.max_resends - data.resend_count;
        let message = data.message || 'Verification email sent successfully!';
        
        if (remainingResends > 0) {
          message += ` (${remainingResends} resends remaining today)`;
        }
        
        setResendVerificationMessage(message);
        setTimeout(() => {
          setShowResendVerification(false);
          setResendVerificationMessage('');
        }, 5000);
      } else {
        const errorData = await response.json();
        
        // Handle different error response formats
        let errorMessage = 'Failed to send verification email.';
        
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
        } else if (errorData.detail && typeof errorData.detail === 'object') {
          errorMessage = errorData.detail.message || errorData.detail.msg || JSON.stringify(errorData.detail);
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
        
        setResendVerificationMessage(errorMessage);
      }
    } catch (error) {
      setResendVerificationMessage('Network error. Please try again.');
    } finally {
      setResendVerificationLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.website) {
      setError('Invalid submission. Please try again.');
      return;
    }
    const elapsedSec = (Date.now() - formLoadTimeRef.current) / 1000;
    if (elapsedSec < 3) {
      setError('Please take a moment before submitting.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(API_CONFIG.getApiUrl(API_CONFIG.endpoints.login), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          form_load_time: formLoadTimeRef.current
        }),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('token_type', data.token_type);
        
        // Get user info
        const userResponse = await fetch(API_CONFIG.getApiUrl(API_CONFIG.endpoints.me), {
          headers: {
            'Authorization': `Bearer ${data.access_token}`
          }
        });
        
        if (userResponse.ok) {
          let userData = await userResponse.json();

          localStorage.setItem('user', JSON.stringify(userData));
          
          // If there's an invite token, show GDPR consent modal before accepting
          if (inviteToken) {
            setShowGdprConsentModal(true);
            // Don't proceed with login success yet - wait for GDPR consent
            return;
          } else {
            onLoginSuccess(userData);
          }
          
          navigate('/dashboard');
        } else {
          throw new Error('Failed to fetch user info');
        }
      } else {
        const errorData = await response.json();
        
        // Handle different error response formats
        let errorMessage = 'Login failed';
        
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
        } else if (errorData.detail && typeof errorData.detail === 'object') {
          errorMessage = errorData.detail.message || errorData.detail.msg || JSON.stringify(errorData.detail);
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
        
        // Check if it's an email verification error
        const verificationHeader = response.headers.get('X-Verification-Required');
        
        // Check for email verification error by status + header OR by error message content
        const isEmailVerificationError = (response.status === 403 && verificationHeader === 'true') || 
                                       errorMessage.toLowerCase().includes('email not verified');
        
        // Check for registration status error (pending/rejected employer registration)
        const registrationStatusHeader = response.headers.get('X-Registration-Status');
        const isRegistrationStatusError = (response.status === 403 && registrationStatusHeader) ||
                                        errorMessage.toLowerCase().includes('registration is pending') ||
                                        errorMessage.toLowerCase().includes('registration has been rejected');
        
        if (isEmailVerificationError) {
          setError(errorMessage);
          setShowResendVerification(true);
        } else if (isRegistrationStatusError) {
          // Show registration status error with appropriate styling
          setError(errorMessage);
        } else {
          setError(errorMessage);
        }
      }
    } catch (err) {
      if (err.message === 'Failed to fetch user info') {
        setError('Login successful but failed to load user information. Please try again.');
      } else {
        setError('Network error. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInviteWithConsent = async () => {
    if (!gdprConsentGiven) {
      setError('Please confirm GDPR consent to accept the invitation');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      
      const acceptResponse = await fetch(
        API_CONFIG.getApiUrl(`/unified-auth/invite/accept/${inviteToken}`),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ gdpr_consent_given: true })
        }
      );
      
      if (acceptResponse.ok) {
        // Refresh user data to get updated relationships
        const updatedUserResponse = await fetch(API_CONFIG.getApiUrl(API_CONFIG.endpoints.me), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (updatedUserResponse.ok) {
          const updatedUserData = await updatedUserResponse.json();
          localStorage.setItem('user', JSON.stringify(updatedUserData));
          setShowGdprConsentModal(false);
          setGdprConsentGiven(false);
          onLoginSuccess(updatedUserData);
          // Clear invite token from URL
          navigate(window.location.pathname, { replace: true });
        } else {
          setError('Failed to refresh user data after accepting invite');
          setShowGdprConsentModal(false);
          onLoginSuccess(userData);
        }
      } else {
        const errorData = await acceptResponse.json();
        setError(errorData.detail || 'Failed to accept invitation');
      }
    } catch (err) {
      setError('Network error while accepting invitation. Please try again.');
    }
  };

  return (
    <div className="login-form-container">
      <Helmet>
        <title>Login | PayCIS</title>
        <meta name="description" content="Sign in to your PayCIS account to manage CIS deductions, subcontractor payments, and HMRC compliance." />
        <meta property="og:title" content="Login | PayCIS" />
      </Helmet>
      <div className="login-form">
        <div className="form-header">
          <h2>Welcome Back</h2>
          <p>{inviteInfo ? `You've been invited by ${inviteEmployerOrgLabel}. Please log in to accept.` : 'Sign in to your PayCIS account'}</p>
        </div>
        
        {inviteInfo && (
          <div style={{
            padding: '15px',
            marginBottom: '20px',
            background: 'rgba(91, 192, 222, 0.1)',
            border: '1px solid rgba(91, 192, 222, 0.3)',
            borderRadius: '8px',
            color: '#57bedd'
          }}>
            <strong>✓ Invite Valid</strong>
            <p style={{ margin: '5px 0 0 0', fontSize: '14px' }}>
              After logging in, you'll be automatically connected to {inviteEmployerOrgLabel}.
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="form">
          {/* Honeypot */}
          <div style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }} aria-hidden="true">
            <label htmlFor="website">Website</label>
            <input
              type="text"
              id="website"
              name="website"
              value={formData.website}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              autoComplete="email"
              required
              placeholder="Enter your email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-container">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                autoComplete="current-password"
                required
                placeholder="Enter your password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="form-options">
            <label className="checkbox-label">
              <input type="checkbox" />
              {/* <span className="checkmark"></span> */}
              {/* Remember me */}
            </label>
            <button type="button" className="forgot-password" onClick={() => setShowForgotPassword(true)}>Forgot Password?</button>
          </div>

          <button 
            type="submit" 
            className="submit-btn"
            disabled={loading}
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </button>
          
          {error && (
            <div className="error-message" style={{ flexDirection: 'column' }}>
              {error}
              {showResendVerification && (
                <div className="resend-verification">
                  <p className="resend-text">
                    Click here to resend verification email to <strong>{formData.email}</strong>
                  </p>
                  <button 
                    type="button" 
                    className="resend-btn"
                    onClick={handleResendVerification}
                    disabled={resendVerificationLoading}
                  >
                    {resendVerificationLoading ? 'Sending...' : 'Resend Verification Email'}
                  </button>
                  {resendVerificationMessage && (
                    <div className="resend-message">{resendVerificationMessage}</div>
                  )}
                </div>
              )}
            </div>
          )}
        </form>

        <div className="form-footer">
          <p>Don't have an account? <button type="button" className="register-link" onClick={() => navigate('/register')}>Register</button></p>
        </div>

        {/* Forgot Password Modal */}
        {showForgotPassword && (
          <div className="forgot-password-modal">
            <div className="forgot-password-content">
              <h3>Reset Password</h3>
              <p>Enter your email address and we'll send you instructions to reset your password.</p>
              
              {forgotPasswordMessage && (
                <div className={`forgot-password-message ${forgotPasswordMessage.includes('sent') ? 'success' : 'error'}`}>
                  {forgotPasswordMessage}
                </div>
              )}

              <form onSubmit={handleForgotPassword}>
                <div className="form-group">
                  <label htmlFor="forgot-email">Email Address</label>
                  <input
                    type="email"
                    id="forgot-email"
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    required
                    placeholder="Enter your email"
                  />
                </div>

                <div className="forgot-password-buttons">
                  <button 
                    type="submit" 
                    className="submit-btn"
                    disabled={forgotPasswordLoading}
                  >
                    {forgotPasswordLoading ? 'Sending...' : 'Send Reset Instructions'}
                  </button>
                  <button 
                    type="button" 
                    className="cancel-btn"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setForgotPasswordEmail('');
                      setForgotPasswordMessage('');
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* GDPR Consent Modal for Invite Acceptance */}
        {showGdprConsentModal && inviteInfo && (
          <div className="modal-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div className="modal-content" style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '30px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}>
              <h2 style={{ marginTop: 0, marginBottom: '20px' }}>Before Connecting</h2>
              
              <div style={{ 
                backgroundColor: '#f8f9fa', 
                padding: '16px', 
                borderRadius: '8px', 
                marginBottom: '20px',
                border: '1px solid #dee2e6',
                color: '#333'
              }}>
                <h3 style={{ marginTop: 0, marginBottom: '12px', fontSize: '16px', fontWeight: 'bold', color: '#333' }}>
                  Data Sharing Consent
                </h3>
                <p style={{ marginBottom: '12px', fontSize: '14px', lineHeight: '1.6', color: '#495057' }}>
                  By accepting this invitation, you authorise PayCIS to share your registration and tax information 
                  (name, business details, UTR, NI, verification status, payment information) with the contractor 
                  listed below for the purpose of verifying your CIS status and processing payments.
                </p>
                <p style={{ marginBottom: '12px', fontSize: '14px', lineHeight: '1.6', color: '#495057' }}>
                  This data will be accessible only to that contractor's authorised users and will not be visible to others. 
                  You can remove the connection at any time from your account.
                </p>
                <div style={{ marginTop: '16px' }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={gdprConsentGiven}
                      onChange={(e) => setGdprConsentGiven(e.target.checked)}
                      style={{ marginTop: '4px', marginRight: '8px', flexShrink: 0 }}
                    />
                    <span style={{ fontSize: '14px', lineHeight: '1.5', color: '#495057' }}>
                      I confirm that I authorise PayCIS to share my registration and tax information with {inviteInfo.employer_company_name || inviteInfo.employer_name || 'the employer'} 
                      &nbsp;for the purpose of verifying my CIS status and processing payments.
                    </span>
                  </label>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button
                  onClick={() => {
                    setShowGdprConsentModal(false);
                    setGdprConsentGiven(false);
                    // Still proceed with login but don't accept invite
                    const userData = JSON.parse(localStorage.getItem('user') || '{}');
                    onLoginSuccess(userData);
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Skip for Now
                </button>
                <button
                  onClick={handleAcceptInviteWithConsent}
                  disabled={!gdprConsentGiven}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: gdprConsentGiven ? '#57bedd' : '#ccc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: gdprConsentGiven ? 'pointer' : 'not-allowed',
                    opacity: gdprConsentGiven ? 1 : 0.6
                  }}
                >
                  Accept Invitation
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginForm;
